import * as React from "react";
import styled, { css } from "styled-components";

import { PaginationButtons } from "src/library/components/Button";
import { Select } from "src/library/components/Forms";
import { Text } from "src/library/components/Text";
import { formatNumber } from "src/library/utils";

interface IPaginationProps {
  className?: string;
  tableOf?: string;
  page: number;
  pages: number;
  pageSize: number;
  pageSizeOptions?: number[];
  totalRows: number;
  nextText: string;
  previousText: string;
  onPageChange: (page: number) => void;
  onPageSizeChange: (pageSize: number) => void;
}

export interface ITablePaginationRefProps {
  onPageChange: (page: number) => void;
  currentPage: number;
}

const TablePagination: React.ForwardRefRenderFunction<ITablePaginationRefProps, IPaginationProps> = (props, ref) => {
  const {
    className,
    page,
    pages,
    totalRows,
    pageSizeOptions,
    pageSize,
    nextText,
    previousText,
    onPageChange,
    onPageSizeChange
  } = props;

  let { tableOf } = props;
  const totalRowsCount = totalRows;
  let pageNum = 0;
  let pagesCount = 0;
  let currentAmount = 0;
  let ofAmount = pageSize;
  let totalRowsCountFormatted = "0";
  let showText = "";

  tableOf = tableOf || "rows";
  pageNum = totalRowsCount > 0 ? page + 1 : 0;
  pagesCount = totalRowsCount > 0 ? pages : 0;
  totalRowsCountFormatted = formatNumber(totalRowsCount);
  currentAmount = pageSize * (pageNum - 1);
  ofAmount = pageSize * (pageNum - 1) + pageSize;

  // Set the human readable text accordingly
  if (pagesCount > 1) {
    showText = `Showing ${currentAmount + 1} - ${totalRowsCount < ofAmount ? totalRowsCount : ofAmount} of ${totalRowsCountFormatted}`;
  } else {
    showText = `Showing ${totalRowsCountFormatted}`;
  }

  showText = `${showText} ${tableOf}.`;

  React.useImperativeHandle<ITablePaginationRefProps, ITablePaginationRefProps>(
    ref,
    () => ({
      onPageChange: page => {
        onPageChange(page);
      },
      currentPage: page
    }),
    [ onPageChange, page ]
  );

  return (
    <TablePaginationStyled className={`rt-pagination ${className}`}>
      <Text
        as="p"
        className="showing"
        fontSize="h4"
      >
        {showText}
      </Text>

      <div className="paginator-container">
        {pagesCount > 1 && (
          <PaginationButtons
            pages={pagesCount}
            page={pageNum}
            buttons={{
              previous: {
                title: previousText,
                onClick: () => onPageChange(page - 1)
              },
              next: {
                title: nextText,
                onClick: () => {
                  onPageChange(page + 1);
                }
              },
              items: Array(pagesCount)
                .fill(pagesCount)
                .map((_p, i) => {
                  return {
                    title: `${i + 1}`,
                    onClick: () => onPageChange(i)
                  };
                })
            }}
          />
        )}

        {pageSizeOptions && (
          <div className="select-container">
            <Select
              as="select"
              name="per-page"
              label="/ page"
              options={pageSizeOptions.map(sizeOpt => {
                return {
                  checked: pageSize === sizeOpt,
                  text: `${sizeOpt} / page`,
                  value: `${sizeOpt}`
                };
              })}
              value={pageSize.toString()}
              handleChange={event => onPageSizeChange(parseInt(event.currentTarget.value, 0))}
            />
          </div>
        )}
      </div>
    </TablePaginationStyled>
  );
};

const TablePaginationStyled = styled.div`${({ theme }) => css`
  display: flex;
  align-items: top;
  padding-top: ${theme.spacing.padding.sm};
  padding-left: ${theme.spacing.padding.sm};
  min-height: 42px;

  &.large {
    flex-direction: row;
    justify-content: space-between;
  }

  &.small {
    flex-direction: column;
  }

  .showing {
    margin: 0;
    padding: ${theme.spacing.padding.xs} 0 0;
    font-size: ${theme.fonts.sizes.h4};
  }

  .paginator-container {
    display: flex;
    flex-direction: row;
    align-items: top;
    position: relative;

    .next-prev,
    .paginators {
      margin: 0;
      padding: 0;

      li {
        margin: 0;
        padding: 0;
      }
    }

    .select-container {
      padding-left: ${theme.spacing.padding.sm};

      > div {
        margin: 0;
      }

      label {
        display: none;
      }

      select {
        margin: 0;
        height: 32px;
        line-height: 1em;
      }
    }
  }
`}`;

export default React.forwardRef(TablePagination);
